import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, FileText, Star, Sparkles, ArrowRight, Printer } from "lucide-react";
import BusinessImpactPanel from "@/components/BusinessImpactPanel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VALORES = [
  { k: "meritocracia", h: "Crecemos por Meritocracia", b: "Cada nivel se gana. Lo medimos con transparencia y los mismos criterios para todos." },
  { k: "autenticos", h: "Somos Auténticos", b: "Sin cajas negras. Cada indicador, cada rúbrica y cada decisión vive a la vista." },
  { k: "no-atajos", h: "No Tomamos Atajos", b: "Cuando Lala te reconoce, no es un sello cómodo: es el resultado de elegir el camino correcto." },
  { k: "resultados", h: "Damos Resultados", b: "Avanzar significa entregar. Premiamos el cumplimiento ambicioso que se queda y crece." },
  { k: "ejemplo", h: "Somos Ejemplo", b: "Los proveedores Elite son referencia. Inspiran a toda la cadena de valor." },
];

export default function ProgramManifesto() {
  const [narrative, setNarrative] = useState(null);

  useEffect(() => {
    fetch(`${API}/public/pillar-narratives`)
      .then((r) => r.json())
      .then(setNarrative)
      .catch(() => {});
  }, []);

  const onPrint = () => window.print();

  return (
    <div className="max-w-5xl mx-auto px-6 py-10" data-testid="program-manifesto-page">
      <div className="flex items-center justify-between gap-3 mb-6 print:hidden">
        <Link to="/" className="text-sm text-[var(--lala-blue)] hover:underline inline-flex items-center gap-1">
          <ArrowLeft size={14} /> Volver al inicio
        </Link>
        <button onClick={onPrint} className="sketch-pill bg-white border border-[var(--lala-blue)] text-[var(--lala-blue)] px-3 py-2 text-sm font-semibold inline-flex items-center gap-2" data-testid="print-manifesto-btn">
          <Printer size={14} /> Imprimir manifiesto
        </button>
      </div>

      {/* Cover */}
      <header className="text-center mb-12">
        <div className="font-hand text-2xl text-[var(--lala-blue)]">el manifiesto vivo del programa</div>
        <h1 className="font-display text-5xl md:text-6xl mt-2">
          LALA <span className="hand-underline">Elite</span>
        </h1>
        <div className="font-hand text-2xl text-[var(--lala-blue)] mt-2">construidos para crecer juntos</div>
        <p className="text-[var(--ink-soft)] mt-4 max-w-2xl mx-auto leading-relaxed">
          Esta es la fuente oficial del programa: cómo evaluamos, qué buscamos, cómo impactamos el negocio
          y cómo crecemos junto con nuestros proveedores estratégicos.
        </p>
      </header>

      {/* Journey */}
      {narrative?.journey?.length > 0 && (
        <section className="sketch-border bg-[var(--cream-soft)] p-8 mb-10" data-testid="manifesto-journey">
          <div className="font-hand text-xl text-[var(--lala-blue)]">así trabajamos contigo</div>
          <h2 className="font-display text-3xl md:text-4xl mt-1">{narrative.headline}</h2>
          <p className="text-[var(--ink-soft)] mt-3 leading-relaxed max-w-3xl">{narrative.subhead}</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-6">
            {narrative.journey.map((j, i) => (
              <div key={j.step} className="bg-white border border-[var(--line)] rounded-xl p-4 relative">
                <div className="absolute -top-3 left-4 px-2 py-0.5 rounded-full bg-[var(--lala-blue)] text-white text-[10px] uppercase tracking-widest font-bold">paso {i + 1}</div>
                <div className="font-display text-xl text-[var(--lala-blue)] mt-2 flex items-center gap-2">
                  {j.step}
                  {i < narrative.journey.length - 1 && <ArrowRight size={14} className="text-[var(--ink-soft)]" />}
                </div>
                <p className="text-xs text-[var(--ink-soft)] mt-1 leading-relaxed">{j.description}</p>
              </div>
            ))}
          </div>
          {narrative.purpose && (
            <div className="mt-6 pt-6 border-t border-[var(--line)]">
              <div className="font-hand text-lg text-[var(--lala-blue)]">el objetivo del programa</div>
              <p className="text-[var(--ink)] mt-1 leading-relaxed max-w-3xl">{narrative.purpose}</p>
            </div>
          )}
        </section>
      )}

      {/* Pillars business impact — auto open */}
      <section className="mb-10" data-testid="manifesto-pillars">
        <div className="text-center mb-6">
          <div className="font-hand text-xl text-[var(--lala-blue)]">seis pilares · seis frases</div>
          <h2 className="font-display text-3xl md:text-4xl mt-1">Cada pilar tiene una promesa.</h2>
          <p className="text-[var(--ink-soft)] mt-3 max-w-3xl mx-auto">
            Las definiciones, frases de campaña y mensajes ejecutivos son los oficiales — la voz LALA
            que comparte con sus stakeholders y proveedores.
          </p>
        </div>
        <BusinessImpactPanel />
      </section>

      {/* Valores LALA */}
      <section className="sketch-border bg-[var(--cream-soft)] p-8 mb-10" data-testid="manifesto-valores">
        <div className="text-center mb-6">
          <div className="font-hand text-xl text-[var(--lala-blue)]">los valores que compartimos</div>
          <h2 className="font-display text-3xl md:text-4xl mt-1">La cultura LALA, vivida en cada relación.</h2>
          <p className="text-[var(--ink-soft)] mt-3 max-w-3xl mx-auto">
            Los 9 valores oficiales del Código de Conducta Lala se traducen en cómo opera el programa.
            Estos son los 5 que más se viven con la familia Elite.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {VALORES.map((v) => (
            <div key={v.k} className="bg-white border border-[var(--line)] rounded-xl p-4">
              <div className="font-hand text-base text-[var(--lala-blue)]">valor oficial Lala</div>
              <h3 className="font-display text-lg mt-0.5">{v.h}</h3>
              <p className="text-xs text-[var(--ink-soft)] mt-1 leading-relaxed">{v.b}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Closing */}
      <section className="text-center py-12 print:py-6" data-testid="manifesto-closing">
        <Sparkles size={32} className="mx-auto text-[var(--lala-blue)]" />
        <div className="font-hand text-2xl text-[var(--lala-blue)] mt-3">la firma del programa</div>
        <p className="font-display text-2xl md:text-3xl mt-2 max-w-3xl mx-auto leading-tight">
          Porque alimentar tu vida es nuestra pasión —<br />
          <span className="text-[var(--lala-blue)]">y eso lo hacemos juntos.</span>
        </p>
        <div className="mt-6 flex flex-wrap gap-3 justify-center print:hidden">
          <Link to="/directorio" className="sketch-pill bg-white border border-[var(--lala-blue)] text-[var(--lala-blue)] px-4 py-2 text-sm font-semibold inline-flex items-center gap-2">
            <Star size={14} /> Ver la familia Elite
          </Link>
          <Link to="/admin/catalogo-kpis" className="sketch-pill bg-[var(--lala-blue)] text-white px-4 py-2 text-sm font-semibold inline-flex items-center gap-2">
            <FileText size={14} /> Ver el catálogo de KPIs
          </Link>
        </div>
      </section>
    </div>
  );
}
